import { X as XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Modal from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { IconBadge } from '@/components/ui/IconBadge';
import { SubmissionIcon } from '@/components/submit/SubmissionIcon';
import { useSubmissionCategories } from '@/hooks/useSubmissionCategories';
import { useUiStore } from '@/store/uiStore';
import { formatPoints } from '@/lib/utils';

export function SubmissionCategoryModal() {
  const open = useUiStore((s) => s.categoryModalOpen);
  const activeKey = useUiStore((s) => s.activeCategoryKey);
  const close = useUiStore((s) => s.closeCategoryDetail);
  const navigate = useNavigate();

  const { data: categories } = useSubmissionCategories();
  const category = categories?.find((c) => c.key === activeKey);

  if (!category) return null;

  const titleId = `category-modal-title-${category.key}`;

  const onSubmit = () => {
    navigate(`/submit?category=${category.key}`);
    close();
  };

  return (
    <Modal open={open} onClose={close} labelledBy={titleId} className="w-full max-w-md" showClose={false}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line bg-white px-6 py-4">
        <h2 id={titleId} className="font-display text-xl font-bold tracking-tightish text-ink-950">
          {category.label}
        </h2>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate2-600 transition-colors hover:bg-paper-warm hover:text-ink-950"
        >
          <XIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Body */}
      <div className="space-y-4 px-6 py-5">
        <div className="flex items-start gap-4">
          <IconBadge size="lg" tone="cyan">
            <SubmissionIcon name={category.icon} size={24} strokeWidth={1.75} />
          </IconBadge>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {category.basePoints > 0 ? (
              <Badge variant="points">{`+${formatPoints(category.basePoints)} pts`}</Badge>
            ) : null}
            {category.highValue ? <Badge variant="locked">1/day</Badge> : null}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate2-600">
            Description
          </p>
          <div className="mt-2 max-h-40 overflow-y-auto scrollbar-thin rounded-xl border border-line bg-paper-warm/60 p-4">
            <p className="text-sm leading-relaxed text-slate2-700">{category.hint}</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-line bg-white px-6 py-4">
        <button type="button" onClick={onSubmit} className="btn-water w-full justify-center">
          Submit a photo
        </button>
      </div>
    </Modal>
  );
}

export default SubmissionCategoryModal;
